import { useEffect, useState, useCallback, useRef } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

// Zlúči uložené poradie s aktuálnym zoznamom položiek: zachová uložené poradie
// pre známe položky a NOVÉ položky pridá na koniec (nerozbije staré preferencie).
export function mergeOrder(saved, defaults) {
  const dset = new Set(defaults);
  const out = (saved || []).filter((id) => dset.has(id));
  defaults.forEach((id, i) => {
    if (out.includes(id)) return;
    const prev = defaults.slice(0, i).reverse().find((d) => out.includes(d));
    out.splice(prev ? out.indexOf(prev) + 1 : 0, 0, id);
  });
  return out;
}

// Aplikuje preusporiadanie viditeľných položiek späť do plného poradia
// (skryté položky si zachovajú svoju pozíciu).
export function applyVisibleReorder(fullOrder, newVisible, isVisible) {
  const q = [...newVisible];
  return fullOrder.map((id) => (isVisible(id) ? q.shift() : id));
}

// Poradie sa ukladá ako preferencia používateľa (per user + firma) na backende.
// Zmena UI je okamžitá (optimistic), uloženie beží na pozadí bez reloadu stránky.
export function useLayoutOrder(key, defaultIds) {
  const [order, setOrder] = useState(defaultIds);
  const defaultsRef = useRef(defaultIds);
  defaultsRef.current = defaultIds;

  useEffect(() => {
    let alive = true;
    api.get("/company/ui-layout")
      .then((r) => { if (alive) setOrder(mergeOrder((r.data.layouts || {})[key], defaultsRef.current)); })
      .catch(() => { if (alive) setOrder(defaultsRef.current); });
    return () => { alive = false; };
  }, [key]);

  const persist = useCallback((next) => {
    api.put("/company/ui-layout", { key, order: next }).catch(() => {});
  }, [key]);

  const update = useCallback((next) => { setOrder(next); persist(next); }, [persist]);
  const reset = useCallback(() => { setOrder(defaultsRef.current); persist(defaultsRef.current); }, [persist]);

  return { order, setOrder: update, reset };
}

// Karta v profile kandidáta – potiahnuteľná za drag handle (nemení funkcionalitu obsahu).
export function SortableCard({ id, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={id} dragListener={false} dragControls={controls}
      whileDrag={{ scale: 1.02, boxShadow: "0 12px 30px rgba(15,23,42,0.12)", zIndex: 40 }}
      className="relative">
      <div
        data-testid={`drag-${id}`}
        onPointerDown={(e) => controls.start(e)}
        title="Potiahnutím zmeníte poradie"
        className="absolute left-1.5 top-1.5 z-20 flex h-6 w-6 items-center justify-center rounded-md bg-white/80 text-slate-300 hover:text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing touch-none select-none">
        <GripVertical size={15} />
      </div>
      {children}
    </Reorder.Item>
  );
}

// Tlačidlo na obnovenie predvoleného poradia.
export function ResetOrderButton({ onReset, testId = "reset-order-btn", label = "Obnoviť predvolené poradie" }) {
  return (
    <button type="button" data-testid={testId} onClick={onReset}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-700 transition-colors">
      <RotateCcw size={13} /> {label}
    </button>
  );
}
